import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { PricingService, PricingMode } from "../../lookup/pricing/PricingService";

import { Temperature } from "../Temperature";

import { ProfilePromiseActionService } from "../../profile/impl/ProfilePromiseActionService";
import { ProfileSettingKeys } from "../core/ProfileSettingKeys";
import { ICcyPairLookupService } from "../../lookup/model/ICcyPairLookupService";
import { LimitOrder } from "./LimitOrder";

export interface PercentAwayValues {
  percent: number;
  sort: number;
  temperature: Temperature;
}

export class PercentAwayHelper {
  protected MAX_ABOVE_MARKET: number = -10001;
  protected MIN_BELOW_MARKET: number = 10001;

  protected MAX_INVERT_ABOVE_MARKET: number = 10001;
  protected MIN_INVERT_BELOW_MARKET: number = -10001;

  private marketBarEnabled: boolean = true;
  protected pinTriggeredEnabled: boolean = true;

  constructor(
    protected pricingService: PricingService,
    protected profileService: ProfilePromiseActionService,
    protected ccyPairLookup: ICcyPairLookupService,
  ) {
    this.profileService
      .getProfileConfig(ProfileSettingKeys.userProfileSettingsOrderBlotterPreferences)
      .then((profileValue) => {
        this.mapUserSettings(profileValue);
      });

    this.profileService
      .getProfileConfigUpdates(ProfileSettingKeys.userProfileSettingsOrderBlotterPreferences)
      .subscribe((profileValue) => {
        this.mapUserSettings(profileValue);
      });
  }

  getTemperature(order: LimitOrder): Temperature {
    if (order["isMarketBar"]) {
      return 0;
    }

    let details = this.calculatePercentageAwayDetails(order, true);
    order.ordTemp = details.temperature;
    order.percSort = details.sort;
    order.perc = details.percent;
    order.tempStale = false;
    return details.temperature;
  }

  updatePercentAway(order: LimitOrder): number {
    if (order["isMarketBar"]) {
      return 0;
    }

    let details = this.calculatePercentageAwayDetails(order, true);
    order.ordTemp = details.temperature;
    order.perc = details.percent;
    order.percSort = details.sort;
    order.tempStale = false;
    return details.percent;
  }

  updateInvertPercentAway(order: LimitOrder): number {
    if (order["isMarketBar"]) {
      return 0;
    }

    let details = this.calculateInvertPercentageAwayDetails(order, true);
    order.ordTemp = details.temperature;
    order.perc = details.percent;
    order.percSort = details.sort;
    order.tempStale = false;
    return details.percent;
  }

  calculateNoTrackingRateInvertAlgoPercentageAwayDetails(
    order: LimitOrder,
    isAboveMarket: boolean,
  ): PercentAwayValues {
    let isTriggered = CubiqTS.OrderState.TRIGGERED == order.orderState;
    let as_sort = this.pricingService.calculatePinAdjustment(
      order,
      isAboveMarket && this.marketBarEnabled,
      true,
      this.pinTriggeredEnabled,
    );

    let temperature = isTriggered ? Temperature.Triggered : Temperature.Hot;

    return {
      percent: NaN,
      sort: as_sort,
      temperature: temperature,
    };
  }

  calculateNoTrackingRateAlgoPercentageAwayDetails(
    order: LimitOrder,
    isAboveMarket: boolean,
  ): PercentAwayValues {
    let isTriggered = CubiqTS.OrderState.TRIGGERED == order.orderState;
    let as_sort = this.pricingService.calculatePinAdjustment(
      order,
      isAboveMarket && this.marketBarEnabled,
      true,
      this.pinTriggeredEnabled,
    );
    let temperature = isTriggered ? Temperature.Triggered : Temperature.Hot;

    return {
      percent: NaN,
      sort: as_sort,
      temperature: temperature,
    };
  }

  calculateNoRatePercentageAwayDetails(
    order: LimitOrder,
    isAboveMarket: boolean,
    isAlgoNoTrackingRate: boolean,
  ): PercentAwayValues {
    let isTriggered = CubiqTS.OrderState.TRIGGERED == order.orderState;
    let as_sort = this.pricingService.calculatePinAdjustment(
      order,
      isAboveMarket && this.marketBarEnabled,
      isAlgoNoTrackingRate,
      this.pinTriggeredEnabled,
    );
    return {
      percent: NaN,
      sort: as_sort,
      temperature: isTriggered ? Temperature.Triggered : Temperature.Cold,
    };
  }

  calculateNoRateInvertPercentageAwayDetails(
    order: LimitOrder,
    isAboveMarket: boolean,
    isAlgoNoTrackingRate: boolean,
  ): PercentAwayValues {
    let isTriggered = CubiqTS.OrderState.TRIGGERED == order.orderState;
    let as_sort = this.pricingService.calculatePinAdjustment(
      order,
      isAboveMarket && this.marketBarEnabled,
      isAlgoNoTrackingRate,
      this.pinTriggeredEnabled,
    );

    return {
      percent: NaN,
      sort: as_sort,
      temperature: isTriggered ? Temperature.Triggered : Temperature.Cold,
    };
  }

  calculatePercentAway(
    market: number,
    tracking: number,
    isAboveMarket: boolean,
  ): { as_sort: number; percAway: number } {
    let percAway = 0;

    if (isAboveMarket) {
      // above market moves away from zero for sort
      percAway = ((tracking - market) / market) * -100;
    } else {
      percAway = ((market - tracking) / market) * 100;
    }

    if (percAway < this.MAX_ABOVE_MARKET) {
      percAway = this.MAX_ABOVE_MARKET;
    } else if (percAway > this.MIN_BELOW_MARKET) {
      percAway = this.MIN_BELOW_MARKET;
    }

    return this.calculatePercentAwayScale(percAway);
  }

  calculateInvertPercentAway(
    market: number,
    tracking: number,
    isAboveMarket: boolean,
  ): { as_sort: number; percAway: number } {
    let percAway = 0;

    if (isAboveMarket) {
      // above market moves away from zero for sort
      percAway = ((tracking - market) / market) * -100;
    } else {
      percAway = ((market - tracking) / market) * 100;
    }

    if (percAway > this.MAX_INVERT_ABOVE_MARKET) {
      percAway = this.MAX_INVERT_ABOVE_MARKET;
    } else if (percAway < this.MIN_INVERT_BELOW_MARKET) {
      percAway = this.MIN_INVERT_BELOW_MARKET;
    }

    return this.calculatePercentAwayScale(percAway * -1);
  }

  calculatePercentAwayScale(value: number): { as_sort: number; percAway: number } {
    value = Number(Math.round(value * 1e9) * 1e-9);

    return {
      as_sort: value,
      percAway: Number(value.toFixed(2)),
    };
  }

  calculatePercentageAwayDetails(order: LimitOrder, shouldFlip: boolean): PercentAwayValues {
    let isAboveMarket: boolean = this.pricingService.isBlotterAboveMarket(order);
    let isAlgo: boolean = this.pricingService.isAlgo(order);
    let isAlgoNoTrackingRate: boolean = isAlgo ? undefined : false;
    let marketFormatted: string = this.pricingService.getBlotterMarketMidRate(order);
    let market: number = NaN;
    let trackingFormatted: string = this.pricingService.getBlotterFwdTracking(order);
    let tracking: number = NaN;
    let isMarketValid: boolean = false;
    let isTrackingValid: boolean = false;

    if (marketFormatted && marketFormatted !== "-") {
      market = parseFloat(marketFormatted);
      isMarketValid = !(Number.isNaN(market) || market == 0);
    }

    if (isMarketValid && trackingFormatted && trackingFormatted !== "-") {
      tracking = parseFloat(trackingFormatted);
      isTrackingValid = !(Number.isNaN(tracking) || tracking == 0);
      isAlgoNoTrackingRate = isAlgo && !isTrackingValid;
    }

    if ((isAlgo || this.pricingService.isMarketOrder(order)) && isMarketValid && !isTrackingValid) {
      return this.calculateNoTrackingRateAlgoPercentageAwayDetails(order, isAboveMarket);
    } else if (!isMarketValid) {
      return this.calculateNoRatePercentageAwayDetails(order, isAboveMarket, isAlgoNoTrackingRate);
    } else {
      let sortAdj = this.pricingService.calculatePinAdjustment(
        order,
        isAboveMarket && this.marketBarEnabled,
        isAlgoNoTrackingRate,
        this.pinTriggeredEnabled,
      );
      let { as_sort, percAway } = this.calculatePercentAway(market, tracking, isAboveMarket);
      as_sort = as_sort + sortAdj;

      // let isTempOnRow: boolean = this.isTempOnRow(order);
      let temperature: Temperature = this.getTemp(order, percAway, market, tracking);

      if (
        temperature !== Temperature.Triggered &&
        this.isThroughMarket(tracking, market, isAboveMarket)
      ) {
        temperature = Temperature.Hot;
      }

      // filp for display formatting only
      if (!shouldFlip)
        //OMS-28631 FOR LADDER
        percAway = percAway * -1;
      else percAway = !this.marketBarEnabled && !isAboveMarket ? percAway : percAway * -1;

      if (!this.marketBarEnabled) {
        as_sort = percAway + sortAdj;
      }

      return {
        percent: percAway,
        sort: as_sort,
        temperature: temperature,
      };
    }
  }

  calculateInvertPercentageAwayDetails(order: LimitOrder, shouldFlip: boolean): PercentAwayValues {
    let isAboveMarket: boolean = !this.pricingService.isBlotterAboveMarket(order);
    let isAlgo: boolean = this.pricingService.isAlgo(order);
    let isAlgoNoTrackingRate: boolean = isAlgo ? undefined : false;
    let marketFormatted: string = this.pricingService.getInvertMarketMidRate(order);
    let market: number = NaN;
    let trackingFormatted: string = this.pricingService.getInvertFwdTracking(order);
    let tracking: number = NaN;
    let isMarketValid: boolean = false;
    let isTrackingValid: boolean = false;
    let pair: CubiqTS.CurrencyPairImpl = this.ccyPairLookup.getCcyPair(
      order.baseCode,
      order.nonBaseCode,
    );

    if (marketFormatted && marketFormatted !== "-") {
      market = parseFloat(marketFormatted);
      isMarketValid = !(Number.isNaN(market) || market == 0);
    }

    if (isMarketValid && trackingFormatted && trackingFormatted !== "-") {
      tracking = parseFloat(trackingFormatted);
      isTrackingValid = !(Number.isNaN(tracking) || tracking == 0);
      isAlgoNoTrackingRate = isAlgo && !isTrackingValid;
    }

    if ((isAlgo || this.pricingService.isMarketOrder(order)) && isMarketValid && !isTrackingValid) {
      return this.calculateNoTrackingRateInvertAlgoPercentageAwayDetails(order, isAboveMarket);
    } else if (!isMarketValid) {
      return this.calculateNoRateInvertPercentageAwayDetails(
        order,
        isAboveMarket,
        isAlgoNoTrackingRate,
      );
    } else {
      let sortAdj = this.pricingService.calculatePinAdjustment(
        order,
        isAboveMarket && this.marketBarEnabled,
        isAlgoNoTrackingRate,
        this.pinTriggeredEnabled,
      );
      let { as_sort, percAway } = this.calculateInvertPercentAway(market, tracking, isAboveMarket);
      as_sort = as_sort + sortAdj;

      // let isTempOnRow: boolean = this.isTempOnRow(order);
      let temperature: Temperature = this.getTemp(order, percAway, market, tracking);

      if (
        temperature !== Temperature.Triggered &&
        this.isThroughMarket(tracking, market, isAboveMarket)
      ) {
        temperature = Temperature.Hot;
      }

      // flip for display formatting only
      if (!shouldFlip)
        //OMS-28631 FOR LADDER
        percAway = percAway * -1;
      else percAway = !this.marketBarEnabled && !isAboveMarket ? percAway : percAway * -1;

      if (!this.marketBarEnabled) {
        as_sort = as_sort + sortAdj;
      }

      return {
        percent: percAway,
        sort: as_sort,
        temperature: temperature,
      };
    }
  }

  isThroughMarket(tracking: number, market: number, isAboveMarket: boolean): boolean {
    return isAboveMarket ? tracking < market : tracking > market;
  }

  getTemp(
    order: CubiqTS.OrderDataImpl,
    percAway: number,
    market: number,
    tracking: number,
  ): Temperature {
    let temp: Temperature = Temperature.Cold;

    if (
      CubiqTS.OrderState.TRIGGERED === order.orderState ||
      CubiqTS.OrderState.TRIGGERED_EXPIRY_ACCEPTANCE_NEEDED === order.orderState
    ) {
      // Note: set percent-away-temp-triggered only!
      temp = Temperature.Triggered;

      return temp;
    } else if (this.pricingService.isAlgo(order) && isNaN(tracking)) {
      temp = Temperature.Hot;

      return temp;
    } else if (isNaN(market) || isNaN(tracking)) {
      temp = Temperature.Cold;

      return temp;
    } else if (this.pricingService.isTempPercents(order)) {
      let warmThreshold = this.pricingService.getTempWarmPercentAway(order);
      let hotThreshold = this.pricingService.getTempHotPercentAway(order);

      // outside warm threshold
      if (percAway > warmThreshold || percAway < -warmThreshold) {
        temp = Temperature.Cold;
        // outside hot threshold
      } else if (percAway > hotThreshold || percAway < -hotThreshold) {
        temp = Temperature.Warm;
      } else {
        temp = Temperature.Hot;
      }

      return temp;
    } else if (this.pricingService.isTempPips(order)) {
      let pipSize = this.pricingService.getTempPipSize(order);

      let warmAboveRate = market + this.pricingService.getTempWarmPipsAway(order) * pipSize;
      let warmBelowRate = market - this.pricingService.getTempWarmPipsAway(order) * pipSize;

      let hotAboveRate = market + this.pricingService.getTempHotPipsAway(order) * pipSize;
      let hotBelowRate = market - this.pricingService.getTempHotPipsAway(order) * pipSize;

      if (tracking > warmAboveRate || tracking < warmBelowRate) {
        temp = Temperature.Cold;
      } else if (tracking > hotAboveRate || tracking < hotBelowRate) {
        temp = Temperature.Warm;
      } else {
        temp = Temperature.Hot;
      }

      return temp;
    }

    return temp;
  }

  isTempOnRow(order: LimitOrder) {
    let isTempOnRow = true;

    if (order && order.orderState >= 0) {
      switch (order.orderState) {
        //
        case CubiqTS.OrderState.TRIGGERED:
        //
        case CubiqTS.OrderState.CANCEL_ACCEPTANCE_NEEDED:
        case CubiqTS.OrderState.MODIFY_ACCEPTANCE_NEEDED:
        case CubiqTS.OrderState.MIGRATION_ACCEPTANCE_NEEDED:
        case CubiqTS.OrderState.EXPIRY_ACCEPTANCE_NEEDED:
        case CubiqTS.OrderState.TRIGGERED_EXPIRY_ACCEPTANCE_NEEDED:
        //
        case CubiqTS.OrderState.ACCEPTANCE_NEEDED:
        case CubiqTS.OrderState.SUSPENDED:
        case CubiqTS.OrderState.PENDING_CANCELLATION:
        //
        case CubiqTS.OrderState.PENDING:
          isTempOnRow = false;
          break;
        case CubiqTS.OrderState.ACTIVE:
        case CubiqTS.OrderState.IN_MODIFICATION:
        default:
          // eligble for temperature or not visible
          break;
      }
    }

    return isTempOnRow;
  }

  public retrieveData(): string {
    // not used
    return null;
  }

  protected mapUserSettings(profileValue: any) {
    if (profileValue) {
      let savedSettings = JSON.parse(profileValue);
      if (savedSettings != undefined && savedSettings != null) {
        this.marketBarEnabled =
          savedSettings.hasOwnProperty("marketBar") &&
          savedSettings.marketBar !== null &&
          savedSettings.marketBar == "y"
            ? true
            : false;
        this.pinTriggeredEnabled =
          savedSettings.hasOwnProperty("pinTriggeredOrdersModel") &&
          savedSettings.pinTriggeredOrdersModel !== null &&
          savedSettings.pinTriggeredOrdersModel == "y"
            ? true
            : false;
      }
    }
  }

  calculateTemperature(order: CubiqTS.OrderDataImpl): Temperature {
    let isAboveMarket: boolean = this.pricingService.isBlotterAboveMarket(order);
    let isAlgo: boolean = this.pricingService.isAlgo(order);
    let isAlgoNoTrackingRate: boolean = isAlgo ? undefined : false;
    let marketFormatted: string = this.pricingService.getBlotterMarketMidRate(order);
    let market: number = NaN;
    let trackingFormatted: string = this.pricingService.getBlotterFwdTracking(order);
    let tracking: number = NaN;
    let isMarketValid: boolean = false;
    let isTrackingValid: boolean = false;
    let pair: CubiqTS.CurrencyPairImpl = this.ccyPairLookup.getCcyPair(
      order.baseCode,
      order.nonBaseCode,
    );

    if (marketFormatted && marketFormatted !== "-") {
      market = parseFloat(marketFormatted);
      isMarketValid = !(Number.isNaN(market) || market == 0);
    }

    if (isMarketValid && trackingFormatted && trackingFormatted !== "-") {
      tracking = parseFloat(trackingFormatted);
      isTrackingValid = !(Number.isNaN(tracking) || tracking == 0);
      isAlgoNoTrackingRate = isAlgo && !isTrackingValid;
    }

    if ((isAlgo || this.pricingService.isMarketOrder(order)) && isMarketValid && !isTrackingValid) {
      return null;
    } else if (!isMarketValid) {
      return null;
    } else {
      const { percAway } = this.calculatePercentAway(market, tracking, isAboveMarket);
      let temperature: Temperature = this.getTemp(order, percAway, market, tracking);

      if (
        temperature !== Temperature.Triggered &&
        this.isThroughMarket(tracking, market, isAboveMarket)
      ) {
        temperature = Temperature.Hot;
      }

      return temperature;
    }
  }
}
